import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Trophy, Medal, MapPin, Flame, Star, Zap, Copy, Snowflake, Users, Share2, Landmark, Crown, Lock, Loader2 } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import UserBadges from "@/components/UserBadges";
import { BadgeStrip } from "@/components/BadgeStrip";
import PushNotificationsCard from "@/components/PushNotificationsCard";

export default function ProfilePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [passport, setPassport] = useState(null);
  const [waymarkClaims, setWaymarkClaims] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, pp, wc] = await Promise.all([
        api.get("/me/profile"),
        api.get("/me/passport"),
        api.get("/me/waymark-claims").catch(() => ({ data: { claims: [], total: 0 } })),
      ]);
      setData(p.data);
      setPassport(pp.data);
      setWaymarkClaims(wc.data);
    })();
  }, []);

  if (!user) return null;
  if (!data) return <div className="py-20 text-[#F5F2E8]/60 text-center">Loading...</div>;

  return (
    <div className="py-8 space-y-8">
      <div className="loot-card-flat p-8 flex flex-col md:flex-row gap-6 items-start">
        <div
          className="w-24 h-24 flex items-center justify-center font-display text-5xl"
          style={{ background: "#123A1A", color: "#76B82A" }}
        >
          {(user.username || user.name || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1">
          <h1 className="font-display uppercase text-4xl tracking-tight inline-flex items-center gap-2 flex-wrap">
            @{user.username}
            <UserBadges
              lootLegend={user.loot_legend}
              goodDeeds={user.good_deeds_count}
              igniter={user.igniter_status}
              badges={user.badges}
              size={14}
              testIdPrefix="me-profile-badge"
            />
          </h1>
          <div className="mt-1">
            <BadgeStrip username={user.username} max={5} size={26} />
          </div>
          <div className="mt-2 text-sm text-[#F5F2E8]/60">{user.name}</div>
          {user.city && (
            <div className="mt-1 font-mono text-xs text-[#F5F2E8]/50 flex items-center gap-1">
              <MapPin size={12} /> {user.city}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <span className="overline">Theme</span>
            <ThemeToggle />
          </div>
        </div>
        {data.stats.streak_days > 0 && (
          <div className="loot-card-flat p-4 text-center" style={{ borderColor: "#E53935" }}>
            <div className="flex items-center gap-1 justify-center text-[#E53935]">
              <Flame size={18} /> <span className="font-display text-3xl">{data.stats.streak_days}</span>
            </div>
            <div className="font-mono text-[10px] text-[#F5F2E8]/60 uppercase tracking-widest mt-1">
              Day streak (30d)
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: "Wins", v: data.stats.wins, color: "#76B82A" },
          { k: "Claims", v: data.stats.claims, color: "#D7A62A" },
          { k: "Votes", v: data.stats.votes, color: "#F5F2E8" },
          { k: "Cities", v: passport?.total || 0, color: "#E53935" },
        ].map((s) => (
          <div key={s.k} className="loot-card-flat p-4">
            <div className="overline">{s.k}</div>
            <div className="font-display text-4xl mt-1" style={{ color: s.color }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Referral + Streak freeze */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="loot-card-flat p-5 space-y-3" data-testid="profile-referral">
          <div className="flex items-center gap-2">
            <Users size={14} style={{ color: "#76B82A" }} />
            <span className="overline">Refer a hunter</span>
            {user.referral_count > 0 && (
              <span className="tag-pill" style={{ color: "#76B82A", borderColor: "#76B82A" }}>
                {user.referral_count} invited
              </span>
            )}
          </div>
          <p className="text-sm text-[#F5F2E8]/70">
            Share your link. You both get a <strong>+1 streak bonus</strong> and the <strong>Recruiter</strong> badge when they sign up.
          </p>
          {user.referral_code ? (
            <>
              <div
                className="font-mono text-xs px-3 py-2 bg-[#1a1a1a] border border-[#76B82A]/30 break-all text-[#F5F2E8]/90"
                data-testid="referral-link"
              >
                {`${window.location.origin}/register?ref=${user.referral_code}`}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <code
                  className="font-mono text-sm px-2 py-1 bg-[#1a1a1a] border border-[#76B82A]/30"
                  data-testid="referral-code"
                >
                  {user.referral_code}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    const url = `${window.location.origin}/register?ref=${user.referral_code}`;
                    navigator.clipboard?.writeText(url);
                    toast.success("Referral link copied");
                  }}
                  className="btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid="referral-copy"
                >
                  <Copy size={12} /> Copy link
                </button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button
                    type="button"
                    onClick={async () => {
                      const url = `${window.location.origin}/register?ref=${user.referral_code}`;
                      try {
                        await navigator.share({
                          title: "Join me on The Local Loot",
                          text: "Real rewards hidden in real cities — cash, gift cards, merch, more. Find it, keep it.",
                          url,
                        });
                      } catch (_e) { /* user cancelled */ }
                    }}
                    className="btn-loot text-xs inline-flex items-center gap-1"
                    data-testid="referral-share"
                  >
                    <Share2 size={12} /> Share
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="font-mono text-xs text-[#F5F2E8]/50">
              Generating your referral link…
            </div>
          )}
        </div>

        <div className="loot-card-flat p-5 space-y-3" data-testid="profile-freeze">
          <div className="flex items-center gap-2">
            <Snowflake size={14} style={{ color: "#76B82A" }} />
            <span className="overline">Streak freeze</span>
            {user.streak_freeze_available ? (
              <span className="tag-pill" style={{ color: "#76B82A", borderColor: "#76B82A" }}>
                Available
              </span>
            ) : (
              <span className="tag-pill" style={{ color: "#F5F2E8", borderColor: "rgba(245,242,232,0.2)" }}>
                Used this month
              </span>
            )}
          </div>
          <p className="text-sm text-[#F5F2E8]/70">
            Save your streak from one bad week. One free freeze per month.
          </p>
          <button
            type="button"
            disabled={!user.streak_freeze_available}
            onClick={async () => {
              try {
                await api.post("/user/streak-freeze");
                toast.success("Streak freeze applied. Your streak is safe.");
                window.location.reload();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Could not apply freeze");
              }
            }}
            className="btn-loot text-xs inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="freeze-btn"
          >
            <Snowflake size={12} /> Use freeze
          </button>
        </div>
      </div>

      {/* Loot Passport */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Loot Passport</div>
          <span className="font-mono text-xs text-[#F5F2E8]/50">
            {passport?.won || 0} cities won · {passport?.total || 0} visited
          </span>
        </div>
        {!passport || passport.stamps.length === 0 ? (
          <div className="loot-card-flat p-6 text-sm text-[#F5F2E8]/60">
            No stamps yet. Open a live drop to earn your first city stamp.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {passport.stamps.map((st) => (
              <div
                key={st.city}
                className="loot-card p-4 relative"
                data-testid={`passport-${st.city}`}
                style={{
                  borderColor: st.won ? "#D7A62A" : undefined,
                  background: st.won ? "rgba(215,166,42,0.06)" : "#0F0F0F",
                }}
              >
                {st.won && (
                  <Star
                    size={16}
                    className="absolute top-2 right-2"
                    style={{ color: "#D7A62A", fill: "#D7A62A" }}
                  />
                )}
                <MapPin size={24} className={st.won ? "text-[#D7A62A]" : "text-[#76B82A]"} />
                <div className="font-display uppercase text-lg mt-2 leading-tight">{st.city}</div>
                <div className="font-mono text-[10px] text-[#F5F2E8]/50 mt-1 uppercase tracking-widest">
                  {st.won ? "Winner stamp" : `${st.visits} visits`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Push notifications — helps hunters get pinged when a drop goes live. */}
      <div>
        <div className="overline mb-3 inline-flex items-center gap-2">
          Notifications
        </div>
        <PushNotificationsCard />
      </div>

      {/* Waymark Passport — permanent pin collections */}
      <div data-testid="waymark-passport">
        <div className="flex items-center justify-between mb-3">
          <div className="overline inline-flex items-center gap-2">
            <Landmark size={12} /> Waymark Passport
          </div>
          <span className="font-mono text-xs text-[#F5F2E8]/50">
            {waymarkClaims?.total || 0} unlocked
          </span>
        </div>
        {!waymarkClaims || waymarkClaims.total === 0 ? (
          <div className="loot-card-flat p-6 text-sm text-[#F5F2E8]/60">
            No waymarks unlocked yet. Walk up to one on the{" "}
            <Link to="/waymarks" className="text-[#76B82A] underline">map</Link>{" "}
            to stamp your passport.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {(waymarkClaims.claims || []).slice(0, 9).map((c) => (
              <div
                key={c.waymark_id}
                className="loot-card p-4"
                style={c.is_sponsored ? { borderColor: "#D7A62A" } : undefined}
              >
                <div className="flex items-center gap-2 text-[#76B82A]">
                  <Landmark size={14} />
                  <span className="overline">{c.category || "waymark"}</span>
                </div>
                <div className="font-display uppercase text-sm mt-2 leading-tight">
                  {c.city || "—"}
                </div>
                {c.is_sponsored && (
                  <div className="font-mono text-[10px] text-[#D7A62A] mt-1 uppercase tracking-widest">
                    Perk collected
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>


      <div>
        <div className="overline mb-3">Badges</div>
        <div className="flex flex-wrap gap-2">
          {(() => {
            // Founder-tier badges get a gold gradient border + crown icon so
            // they stand out visually from regular badges. Other badges keep
            // the existing green pill style.
            const FOUNDER_TIER = ["Found the Founder", "Loot Disciple", "Local Legend"];
            return (user.badges || []).map((b) => {
              if (FOUNDER_TIER.includes(b)) {
                return (
                  <span
                    key={b}
                    className="flex items-center gap-1 px-3 py-1 font-display uppercase text-xs tracking-widest relative"
                    style={{
                      background: "linear-gradient(135deg, #D7A62A, #F5D066, #D7A62A)",
                      color: "#0F0F0F",
                      border: "2px solid #D7A62A",
                      boxShadow: "0 0 12px rgba(215,166,42,0.35)",
                    }}
                    data-testid={`badge-${b.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <Crown size={12} /> {b}
                    <span className="ml-1 font-mono text-[8px] opacity-70">RARE</span>
                  </span>
                );
              }
              return (
                <span
                  key={b}
                  className="tag-pill flex items-center gap-1 px-3 py-1"
                  style={{ color: "#76B82A", borderColor: "#76B82A" }}
                >
                  <Medal size={12} /> {b}
                </span>
              );
            });
          })()}
          {(user.badges || []).length === 0 && <div className="text-[#F5F2E8]/60">No badges yet.</div>}
        </div>
      </div>

      <div>
        <div className="overline mb-3">Recent finds</div>
        <div className="space-y-2">
          {data.recent_wins.length === 0 && <div className="text-[#F5F2E8]/60">No finds yet.</div>}
          {data.recent_wins.map((w) => {
            const pt = w.prize_type || "cash";
            let headline;
            if (w.is_mystery) headline = "🎁 Mystery Loot";
            else if (pt === "cash") headline = w.hide_amount ? "Cash" : `$${w.amount}`;
            else headline = w.prize_custom_label || pt.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
            return (
              <div key={w.id} className="loot-card-flat p-4 flex items-center justify-between">
                <div>
                  <div className="font-display uppercase text-lg">{w.city} · {headline}</div>
                  <div className="text-xs font-mono text-[#F5F2E8]/50">{w.approval_status}</div>
                </div>
                <Trophy className="text-[#D7A62A]" />
              </div>
            );
          })}
        </div>
      </div>

      <ChangePasswordCard />
    </div>
  );
}

// ----------------------------------------------------------------------
// Change Password — secure, authenticated, rotates JWT on success.
// ----------------------------------------------------------------------
function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    if (next === current) {
      toast.error("New password must be different from your current one.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Use the new one next time you log in.");
      reset();
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" · ")
        : (typeof detail === "string" ? detail : "Couldn't change password.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="loot-card-flat p-5" data-testid="profile-change-password">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Lock size={14} style={{ color: "#76B82A" }} />
          <span className="overline">Change password</span>
        </div>
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="btn-ghost text-xs"
            data-testid="profile-change-password-toggle"
          >
            Update
          </button>
        )}
      </div>
      {!open ? (
        <p className="text-sm text-[#F5F2E8]/60 mt-2">
          Pick a new password. We'll require your current password to confirm it's really you.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-3 space-y-3" data-testid="profile-change-password-form">
          <p className="text-[12px] text-[#F5F2E8]/55">
            You'll stay signed in here. Sign out manually on any other devices.
          </p>
          <label className="block">
            <div className="overline mb-1">Current password</div>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="loot-input"
              data-testid="profile-change-password-current"
            />
          </label>
          <label className="block">
            <div className="overline mb-1">New password</div>
            <input
              type="password"
              required
              minLength={6}
              maxLength={200}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="loot-input"
              data-testid="profile-change-password-new"
            />
            <div className="text-[11px] text-[#F5F2E8]/50 mt-1">At least 6 characters.</div>
          </label>
          <label className="block">
            <div className="overline mb-1">Confirm new password</div>
            <input
              type="password"
              required
              minLength={6}
              maxLength={200}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="loot-input"
              data-testid="profile-change-password-confirm"
            />
          </label>
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={busy}
              className="btn-loot inline-flex items-center gap-2"
              data-testid="profile-change-password-submit"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
              {busy ? "Updating…" : "Update password"}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); reset(); }}
              className="btn-ghost text-xs"
              data-testid="profile-change-password-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
